import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Building,
  TrendingUp,
  Users,
  FileText,
  Wrench,
  AlertCircle,
  ArrowRight,
  DollarSign,
  Home,
  Calendar,
  Loader2,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/components/auth-provider";
import { getAllUserProperties } from "@/lib/property-service";
import { getLandlordApplications } from "@/lib/application-service";
import { getAllLandlordLeases } from "@/lib/lease-service";
import { getMaintenanceRequests } from "@/lib/maintenance-service";
import { getInquiryStats } from "@/lib/inquiry-service";
import { toast } from "sonner";
import moment from "moment";
import { DashboardHeader } from "@/components/dashboard/dashboard-header";

interface DashboardStats {
  totalProperties: number;
  occupiedUnits: number;
  totalUnits: number;
  monthlyRevenue: number;
  pendingApplications: number;
  newInquiries: number;
  maintenanceRequests: number;
  upcomingLeaseRenewals: number;
}

export default function LandlordDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalProperties: 0,
    occupiedUnits: 0,
    totalUnits: 0,
    monthlyRevenue: 0,
    pendingApplications: 0,
    newInquiries: 0,
    maintenanceRequests: 0,
    upcomingLeaseRenewals: 0,
  });

  const [recentActivity, setRecentActivity] = useState<
    Array<{
      id: string;
      type: "application" | "maintenance" | "payment" | "lease";
      message: string;
      time: string;
    }>
  >([]);

  const [upcomingTasks, setUpcomingTasks] = useState<
    Array<{
      id: string;
      task: string;
      due: string;
    }>
  >([]);

  const [topProperties, setTopProperties] = useState<
    Array<{
      id: string;
      title: string;
      status: string;
      rent: number;
      occupancyRate: number;
    }>
  >([]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch all data in parallel (using non-paginated functions for stats)
        const [properties, applications, leases, maintenanceData, inquiryData] =
          await Promise.all([
            getAllUserProperties(user.id),
            getLandlordApplications(user.id),
            getAllLandlordLeases(user.id),
            getMaintenanceRequests({ userId: user.id, userRole: "landlord" }),
            getInquiryStats(user.id),
          ]);

        // Calculate stats
        const occupiedCount = properties.filter(
          (p) => p.status === "occupied"
        ).length;
        const totalRevenue = properties.reduce((sum, p) => {
          if (p.status === "occupied") {
            return sum + (p.rent || 0);
          }
          return sum;
        }, 0);

        const pendingApps = applications.filter(
          (app) => app.status === "submitted" || app.status === "under-review"
        ).length;
        const openMaintenance = maintenanceData.filter(
          (req) => req.status !== "completed" && req.status !== "cancelled"
        ).length;

        // Count lease renewals in next 60 days
        const now = moment();
        const upcomingRenewals = leases.filter((lease) => {
          if (lease.status !== "active") return false;
          const endDate = moment(lease.endDate);
          const daysUntilEnd = endDate.diff(now, "days");
          return daysUntilEnd >= 0 && daysUntilEnd <= 60;
        }).length;

        setStats({
          totalProperties: properties.length,
          occupiedUnits: occupiedCount,
          totalUnits: properties.length, // For single-unit properties
          monthlyRevenue: totalRevenue,
          pendingApplications: pendingApps,
          newInquiries: inquiryData.new || 0,
          maintenanceRequests: openMaintenance,
          upcomingLeaseRenewals: upcomingRenewals,
        });

        // Build recent activity from all sources
        const activities: typeof recentActivity = [];

        // Add recent applications
        applications
          .filter(
            (app) => app.status === "submitted" || app.status === "under-review"
          )
          .slice(0, 2)
          .forEach((app) => {
            const property = properties.find((p) => p.id === app.propertyId);
            activities.push({
              id: `app-${app.id}`,
              type: "application",
              message: `New application for ${property?.title || "property"}`,
              time: moment(app.createdAt).fromNow(),
            });
          });

        // Add recent maintenance requests
        maintenanceData
          .filter(
            (req) => req.status === "pending" || req.status === "in-progress"
          )
          .slice(0, 2)
          .forEach((req) => {
            activities.push({
              id: `maint-${req.id}`,
              type: "maintenance",
              message: `Maintenance request: ${req.title}`,
              time: moment(req.createdAt).fromNow(),
            });
          });

        // Add recent active leases
        leases
          .filter((lease) => lease.status === "active")
          .slice(0, 1)
          .forEach((lease) => {
            activities.push({
              id: `lease-${lease.id}`,
              type: "lease",
              message: `Active lease for property`,
              time: moment(lease.startDate).fromNow(),
            });
          });

        // Sort by most recent
        activities.sort((a, b) => {
          const timeA = moment(a.time, "from");
          const timeB = moment(b.time, "from");
          return timeB.diff(timeA);
        });

        setRecentActivity(activities.slice(0, 4));

        // Build upcoming tasks
        const tasks: typeof upcomingTasks = [];

        if (pendingApps > 0) {
          tasks.push({
            id: "task-apps",
            task: `Review ${pendingApps} pending application${
              pendingApps > 1 ? "s" : ""
            }`,
            due: "Today",
          });
        }

        if (openMaintenance > 0) {
          tasks.push({
            id: "task-maint",
            task: `Follow up on ${openMaintenance} maintenance request${
              openMaintenance > 1 ? "s" : ""
            }`,
            due: "This week",
          });
        }

        if (upcomingRenewals > 0) {
          tasks.push({
            id: "task-lease",
            task: `Process ${upcomingRenewals} lease renewal${
              upcomingRenewals > 1 ? "s" : ""
            }`,
            due: "In 60 days",
          });
        }

        // Add property inspection task if there are properties
        if (properties.length > 0) {
          tasks.push({
            id: "task-inspect",
            task: "Schedule property inspections",
            due: "This month",
          });
        }

        setUpcomingTasks(tasks.slice(0, 4));

        // Calculate top properties by rent and status
        const sortedProperties = [...properties]
          .filter((p) => p.status === "occupied" || p.status === "vacant")
          .sort((a, b) => (b.rent || 0) - (a.rent || 0))
          .slice(0, 3)
          .map((p) => ({
            id: p.id,
            title: p.title,
            status: p.status,
            rent: p.rent,
            occupancyRate: p.status === "occupied" ? 100 : 0,
          }));

        setTopProperties(sortedProperties);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.id]);

  const occupancyRate =
    stats.totalUnits > 0 ? (stats.occupiedUnits / stats.totalUnits) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <DashboardHeader
        title="Dashboard"
        subtitle="Welcome back! Here's an overview of your properties."
        actionButton={{
          label: "Add New Property",
          icon: <Building className="mr-2 h-4 w-4" />,
          onClick: () => (window.location.href = "/landlord/properties"),
        }}
      />

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Properties
            </CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalProperties}</div>
            <p className="text-xs text-muted-foreground">
              Across Lagos, Abuja, and Port Harcourt
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Occupancy Rate
            </CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {occupancyRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.occupiedUnits} of {stats.totalUnits} units occupied
            </p>
            <Progress value={occupancyRate} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Monthly Revenue
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ₦{(stats.monthlyRevenue / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              +12.5% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Actions
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.pendingApplications + stats.newInquiries + stats.maintenanceRequests}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.pendingApplications} applications, {stats.newInquiries} inquiries,{" "}
              {stats.maintenanceRequests} maintenance
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Button asChild variant="outline" className="h-auto py-4">
          <Link
            to="/landlord/applications"
            className="flex flex-col items-center gap-2"
          >
            <FileText className="h-6 w-6" />
            <div className="text-center">
              <div className="font-semibold">Applications</div>
              <div className="text-xs text-muted-foreground">
                {stats.pendingApplications} pending
              </div>
            </div>
          </Link>
        </Button>

        <Button asChild variant="outline" className="h-auto py-4">
          <Link
            to="/landlord/inquiries"
            className="flex flex-col items-center gap-2"
          >
            <Mail className="h-6 w-6" />
            <div className="text-center">
              <div className="font-semibold">Inquiries</div>
              <div className="text-xs text-muted-foreground">
                {stats.newInquiries} new
              </div>
            </div>
          </Link>
        </Button>

        <Button asChild variant="outline" className="h-auto py-4">
          <Link
            to="/landlord/maintenance"
            className="flex flex-col items-center gap-2"
          >
            <Wrench className="h-6 w-6" />
            <div className="text-center">
              <div className="font-semibold">Maintenance</div>
              <div className="text-xs text-muted-foreground">
                {stats.maintenanceRequests} open requests
              </div>
            </div>
          </Link>
        </Button>

        <Button asChild variant="outline" className="h-auto py-4">
          <Link
            to="/landlord/leases"
            className="flex flex-col items-center gap-2"
          >
            <Calendar className="h-6 w-6" />
            <div className="text-center">
              <div className="font-semibold">Lease Renewals</div>
              <div className="text-xs text-muted-foreground">
                {stats.upcomingLeaseRenewals} upcoming
              </div>
            </div>
          </Link>
        </Button>

        <Button asChild variant="outline" className="h-auto py-4">
          <Link
            to="/landlord/tenants"
            className="flex flex-col items-center gap-2"
          >
            <Users className="h-6 w-6" />
            <div className="text-center">
              <div className="font-semibold">Tenants</div>
              <div className="text-xs text-muted-foreground">
                {stats.occupiedUnits} active
              </div>
            </div>
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Your latest property updates and notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentActivity.length > 0 ? (
              <>
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-start gap-4 pb-4 border-b last:border-0 last:pb-0"
                    >
                      <div className="shrink-0">
                        {activity.type === "application" && (
                          <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-2">
                            <FileText className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                          </div>
                        )}
                        {activity.type === "maintenance" && (
                          <div className="rounded-full bg-orange-100 dark:bg-orange-900 p-2">
                            <Wrench className="h-4 w-4 text-orange-600 dark:text-orange-300" />
                          </div>
                        )}
                        {activity.type === "payment" && (
                          <div className="rounded-full bg-green-100 dark:bg-green-900 p-2">
                            <DollarSign className="h-4 w-4 text-green-600 dark:text-green-300" />
                          </div>
                        )}
                        {activity.type === "lease" && (
                          <div className="rounded-full bg-purple-100 dark:bg-purple-900 p-2">
                            <Calendar className="h-4 w-4 text-purple-600 dark:text-purple-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm">{activity.message}</p>
                        <p className="text-xs text-muted-foreground">
                          {activity.time}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-4" asChild>
                  <Link to="/landlord/activity">
                    View All Activity
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No recent activity</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Tasks */}
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Tasks</CardTitle>
            <CardDescription>
              Important actions that need your attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            {upcomingTasks.length > 0 ? (
              <>
                <div className="space-y-4">
                  {upcomingTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between pb-4 border-b last:border-0 last:pb-0"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <span className="text-sm">{task.task}</span>
                      </div>
                      <Badge variant="outline" className="ml-2">
                        {task.due}
                      </Badge>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-4" asChild>
                  <Link to="/landlord/tasks">
                    View All Tasks
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No pending tasks</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Property Performance Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Property Performance</CardTitle>
          <CardDescription>
            Track your top performing properties this month
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topProperties.length > 0 ? (
            <div className="space-y-4">
              {topProperties.map((property) => {
                const occupancyBadge =
                  property.occupancyRate === 100
                    ? { variant: "default" as const, label: "Occupied" }
                    : { variant: "outline" as const, label: "Vacant" };

                return (
                  <div
                    key={property.id}
                    className="flex items-center justify-between"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{property.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {property.status} • ₦
                        {(property.rent / 1000000).toFixed(1)}M/month
                      </p>
                    </div>
                    <Badge variant={occupancyBadge.variant}>
                      {occupancyBadge.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Building className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No properties yet. Add your first property to get started.</p>
            </div>
          )}
          <div className="space-y-4"></div>
          <Button variant="outline" className="w-full mt-4" asChild>
            <Link to="/landlord/properties">
              View All Properties
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
